import enCommon from "@/i18n/local/en/common";
import enArticle from "@/i18n/local/en/article";
import enPosts from "@/i18n/local/en/posts";
import { pillarArticles } from "@/mocks/articles";
import type { PillarArticle } from "@/mocks/articles";
import { heroBackgroundImage } from "@/mocks/categories";

// The real site origin comes from the environment. Do not invent arbitrary domains.
export const SITE_URL: string =
  (import.meta.env.VITE_SITE_URL as string | undefined) ||
  "https://allchinabuyspread.uk";

export const SITE_NAME = "AllChinaBuy Spread";

export const FAVICON_URL =
  "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/14bc629c50c04cb08f38590896596985.jpeg";

// Merged English strings are the canonical, language-stable source of truth
// for head metadata and structured data (independent of the active UI language).
const enAll: Record<string, string> = {
  ...enCommon,
  ...enArticle,
  ...enPosts,
};

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string | null;
  ogType: string;
  image?: string;
}

export interface FaqItem {
  q: string;
  a: string;
}

const HOME_KEYWORDS =
  "AllChinaBuy Spreadsheet, AllChinaBuy review, AllChinaBuy shipping cost, AllChinaBuy QC, AllChinaBuy coupon";

const HOME_SEO: SeoData = {
  title: "AllChinaBuy Spreadsheet | QC, Shipping & Buying Guides",
  description:
    "Independent AllChinaBuy Spreadsheet hub for product research, QC photos, shipping cost guides, coupons and safety tips. Explore AllChinaBuy spreadsheet, products, shipping and reviews.",
  keywords: HOME_KEYWORDS,
  canonicalPath: "/",
  ogType: "website",
  image: heroBackgroundImage,
};

const ARTICLE_KEYWORDS =
  "AllChinaBuy Spreadsheet, AllChinaBuy review, AllChinaBuy shipping, AllChinaBuy QC, AllChinaBuy coupon";

function buildArticleSeo(article: PillarArticle): SeoData {
  return {
    title: enAll[`${article.keyPrefix}.title`],
    description: enAll[`${article.keyPrefix}.lead`],
    keywords: ARTICLE_KEYWORDS,
    canonicalPath: `/guides/${article.slug}`,
    ogType: "article",
    image: article.coverImage,
  };
}

export function getSeoForPath(pathname: string): SeoData | null {
  if (pathname === "/" || pathname === "") {
    return HOME_SEO;
  }

  // Legacy redirect target resolves to the guide article before navigation settles.
  if (pathname === "/allchinabuy-spreadsheet-guide") {
    const guide = pillarArticles.find(
      (a) => a.slug === "allchinabuy-spreadsheet-guide",
    );
    return guide ? buildArticleSeo(guide) : HOME_SEO;
  }

  const match = pathname.match(/^\/guides\/([^/]+)$/);
  if (match) {
    const article = pillarArticles.find((a) => a.slug === match[1]);
    if (article) {
      return buildArticleSeo(article);
    }
  }

  return null;
}

export function getFaqForPath(pathname: string): FaqItem[] {
  if (pathname === "/" || pathname === "") {
    return Array.from({ length: 5 }, (_, i) => ({
      q: enAll[`faq.q${i + 1}`],
      a: enAll[`faq.a${i + 1}`],
    }));
  }

  const match = pathname.match(/^\/guides\/([^/]+)$/);
  if (match) {
    const article = pillarArticles.find((a) => a.slug === match[1]);
    if (article) {
      return Array.from({ length: article.faqCount }, (_, i) => ({
        q: enAll[`${article.keyPrefix}.faq.q${i + 1}`],
        a: enAll[`${article.keyPrefix}.faq.a${i + 1}`],
      }));
    }
  }

  return [];
}